import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, InlineStack, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { useParams } from "react-router-dom";
import { fetchFileDetails } from "containers/file/file.Slice";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";
import { resetFileSignatures } from "services/upload-file.service";
import { Textarea } from "catalyst-ui-kit/textarea";
import { toast } from "sonner";

interface ResetFileSignaturesConfirmProps {
  fileId: string;
  /** When true the document is fully signed — resetting captures an
   * evidence snapshot and notifies signers/receivers, so show that notice
   * and let the user give an (optional) reason. */
  isFullySigned?: boolean;
  /** Lets the caller refresh anything that isn't redux-backed (e.g. DocumentAnnulmentPanel). */
  onDone?: () => void;
}

const ResetFileSignaturesConfirm: React.FC<ResetFileSignaturesConfirmProps> = ({
  fileId,
  isFullySigned = false,
  onDone,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId, orderId } = useParams();

  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reason, setReason] = useState<string>("");

  const handleConfirm = async () => {
    if (!companyId || !fileId) return;
    try {
      setIsLoading(true);
      await resetFileSignatures(
        companyId,
        fileId,
        isFullySigned ? { reason: reason.trim() || undefined } : undefined
      );
      toast.success(t("remove_signed_document_success"));
      if (orderId) {
        dispatch(fetchOrderDetails({ companyId, orderId }));
      } else {
        dispatch(fetchFileDetails({ fileId, companyId }));
      }
      onDone?.();
      closeModal();
    } catch (e: any) {
      setErrMsg(e?.response?.data?.message || "error");
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  return (
    <>
      <div className="form-warnings">
        {errMsg && (
          <div>
            <Banner tone="critical">{t(errMsg)}</Banner>
          </div>
        )}
      </div>
      <h1 style={{ textAlign: "center", marginBottom: "16px" }}>
        {t("remove_signed_document_confirm")}
      </h1>

      {isFullySigned && (
        <div className="mb-4 space-y-3">
          <Banner tone="warning">{t("reset_signatures_fully_signed_notice")}</Banner>
          <div>
            <label
              htmlFor="reset-signatures-reason"
              className="mb-1 block text-sm font-medium text-zinc-950 dark:text-white"
            >
              {t("annulment_reason_label")}
            </label>
            <Textarea
              id="reset-signatures-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("reset_signatures_reason_placeholder")}
              rows={3}
            />
          </div>
        </div>
      )}

      <InlineStack align="space-between">
        <Button
          onClick={handleConfirm}
          loading={isLoading}
          tone="critical"
          variant="primary"
        >
          {t("Yes")}
        </Button>

        <Button onClick={closeModal}>{t("No")}</Button>
      </InlineStack>
    </>
  );
};

export default ResetFileSignaturesConfirm;
